import React from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, IconButton, MenuItem, Paper, Select, Snackbar,
  Stack, TextField, Tooltip, Typography, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import NotesIcon from '@mui/icons-material/Notes';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import HistoryIcon from '@mui/icons-material/History';

import { apiRequest } from '../../utils/axios-config';
import { rafraichirTaxonomie, ordreNiveaux } from '../../config/shared_config';
import AdminLayout from '../../layouts/AdminLayout';

/**
 * Édition du référentiel de compétences.
 *
 * Chantier « ID de compétence », phase P3b.
 *
 * ─── Ce que cet écran rend possible, et pourquoi c'est le but du chantier ───
 * Renommer une notion coûtait, avant, une migration SQL sur 7 tables qui
 * FUSIONNAIT des maîtrises d'élèves à la moyenne (migration 081). Ici, c'est un
 * champ de texte : l'ID reste, le libellé change, l'ancien devient un alias.
 *
 * Trois choses ne sont volontairement PAS proposées :
 *   • modifier un ID (invariant du chantier) ;
 *   • supprimer une notion (on la déprécie — des élèves l'ont travaillée) ;
 *   • éditer les prompts de génération (PI, hors navigateur).
 *
 * La description pédagogique (résumé parent / détail notion) est éditable ici.
 */
export default function TaxonomyReferentialScreen() {
  const [referentiel, setReferentiel] = React.useState(null);
  const [chargement, setChargement] = React.useState(true);
  const [erreur, setErreur] = React.useState(null);
  const [message, setMessage] = React.useState(null);
  const [audit, setAudit] = React.useState([]);

  // Édition en cours : { type: 'rename'|'reclass'|'level'|'deprecate', notion }
  const [action, setAction] = React.useState(null);
  const [saisie, setSaisie] = React.useState('');
  const [enregistrement, setEnregistrement] = React.useState(false);

  const charger = React.useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const [reponse, journal] = await Promise.all([
        apiRequest('get', '/admin/taxonomy'),
        apiRequest('get', '/admin/taxonomy/audit', { params: { limit: 30 } }),
      ]);
      setReferentiel(reponse.referentiel);
      setAudit(journal.audit || []);
    } catch (e) {
      setErreur(e?.response?.data?.error || e.message || 'Chargement impossible');
    } finally {
      setChargement(false);
    }
  }, []);

  React.useEffect(() => {
    charger();
  }, [charger]);

  /** Toute écriture invalide le cache local : sans cela l'admin verrait l'ancien libellé. */
  async function apresEcriture(texte) {
    setMessage(texte);
    await rafraichirTaxonomie();
    await charger();
  }

  async function executer(appel, texteSucces) {
    setEnregistrement(true);
    try {
      await appel();
      setAction(null);
      await apresEcriture(texteSucces);
    } catch (e) {
      setErreur(e?.response?.data?.error || e.message || 'Écriture refusée');
    } finally {
      setEnregistrement(false);
    }
  }

  const competencesParDomaine = React.useMemo(() => {
    if (!referentiel) return [];
    return [...referentiel.domains]
      .sort((a, b) => (a.ordreAffichage ?? a.ordre) - (b.ordreAffichage ?? b.ordre))
      .map((domaine) => ({
        domaine,
        competences: referentiel.competences
          .filter((c) => c.domainId === domaine.id)
          .sort((a, b) => a.ordre - b.ordre)
          .map((competence) => ({
            competence,
            notions: referentiel.notions
              .filter((n) => n.competenceId === competence.id)
              .sort((a, b) => a.ordre - b.ordre),
          })),
      }));
  }, [referentiel]);

  const toutesCompetences = referentiel?.competences || [];

  if (chargement && !referentiel) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
    <Box sx={{ p: 1 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h4">Référentiel de compétences</Typography>
        {referentiel && <Chip label={`version ${referentiel.version}`} size="small" />}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Renommer une notion ne touche ni les exercices ni la progression des élèves : son
        identifiant reste le même et l'ancien libellé est conservé comme alias. Une notion
        ne se supprime pas — elle se déprécie. La description est le résumé affiché aux
        parents (et bientôt dans l'app) — distinct du prompt de génération.
      </Typography>

      {erreur && (
        <Alert severity="error" onClose={() => setErreur(null)} sx={{ mb: 2 }}>
          {erreur}
        </Alert>
      )}

      {competencesParDomaine.map(({ domaine, competences }) => (
        <Accordion key={domaine.id} defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
              <Typography variant="h6">{domaine.label}</Typography>
              <Chip size="small" variant="outlined" label={domaine.id} />
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="caption" color="text.secondary">
                {competences.reduce((total, c) => total + c.notions.length, 0)} notions
              </Typography>
            </Stack>
          </AccordionSummary>

          <AccordionDetails>
            {competences.map(({ competence, notions }) => (
              <Paper key={competence.id} variant="outlined" sx={{ mb: 2, p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {competence.label}
                  </Typography>
                  <Chip size="small" variant="outlined" label={competence.id} />
                  <Tooltip title="Renommer la compétence">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setAction({ type: 'rename-competence', cible: competence });
                        setSaisie(competence.label);
                      }}
                    >
                      <EditIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <Divider sx={{ mb: 1 }} />

                <Stack spacing={0.5}>
                  {notions.map((notion) => (
                    <Stack
                      key={notion.id}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ py: 0.5, opacity: notion.actif === false ? 0.45 : 1 }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 320 }}>
                        <Typography component="span">{notion.label}</Typography>
                        {notion.actif === false && (
                          <Chip size="small" color="default" label="dépréciée" />
                        )}
                      </Stack>

                      {/* L'ID est AFFICHÉ mais jamais modifiable : c'est lui que
                          portent les exercices et la progression des élèves. */}
                      <Tooltip title="Identifiant stable — non modifiable">
                        <Chip size="small" variant="outlined" label={notion.id} />
                      </Tooltip>

                      <Chip size="small" label={notion.introducedAtLevel} />

                      {notion.description ? (
                        <Tooltip title={notion.description}>
                          <Chip size="small" color="success" variant="outlined" label="desc." />
                        </Tooltip>
                      ) : (
                        <Chip size="small" variant="outlined" label="sans desc." sx={{ opacity: 0.6 }} />
                      )}

                      <Box sx={{ flexGrow: 1 }} />

                      <Tooltip title="Renommer">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setAction({ type: 'rename', cible: notion });
                            setSaisie(notion.label);
                          }}
                        >
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Éditer la description pédagogique">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setAction({ type: 'description', cible: notion });
                            setSaisie(notion.description || '');
                          }}
                        >
                          <NotesIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Déplacer vers une autre compétence">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setAction({ type: 'reclass', cible: notion });
                            setSaisie(notion.competenceId);
                          }}
                        >
                          <DriveFileMoveIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Changer le niveau d'introduction">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setAction({ type: 'level', cible: notion });
                            setSaisie(notion.introducedAtLevel);
                          }}
                        >
                          <HistoryIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>

                      {notion.actif === false ? (
                        <Tooltip title="Réactiver">
                          <IconButton
                            size="small"
                            onClick={() =>
                              executer(
                                () => apiRequest('post', `/admin/taxonomy/notions/${notion.id}/restore`),
                                `« ${notion.label} » réactivée`
                              )
                            }
                          >
                            <RestoreIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Déprécier (ne supprime pas)">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => {
                              setAction({ type: 'deprecate', cible: notion });
                              setSaisie('');
                            }}
                          >
                            <BlockIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* ─── Journal ─── */}
      <Accordion sx={{ mt: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Dernières modifications</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {audit.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Aucune modification enregistrée.
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {audit.map((ligne) => (
                <Stack
                  key={ligne.id}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Chip size="small" label={ligne.action} />
                  <Typography variant="body2" component="span" fontWeight={600}>
                    {ligne.targetId}
                  </Typography>
                  <Typography variant="body2" component="span">
                    {ligne.avant?.label && ligne.apres?.label
                      ? `« ${ligne.avant.label} » → « ${ligne.apres.label} »`
                      : ''}
                    {ligne.action === 'description'
                      ? (ligne.apres?.description
                        ? 'description mise à jour'
                        : 'description effacée')
                      : ''}
                    {ligne.avant?.competenceId && ligne.apres?.competenceId
                      ? `${ligne.avant.competenceId} → ${ligne.apres.competenceId}`
                      : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="span">
                    {new Date(ligne.creeLe).toLocaleString('fr-FR')}
                    {ligne.adminUid ? ` · ${ligne.adminUid}` : ''}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      {/* ─── Boîtes de dialogue ─── */}
      <Dialog open={!!action} onClose={() => !enregistrement && setAction(null)} fullWidth maxWidth="sm">
        {action?.type === 'rename' && (
          <>
            <DialogTitle>Renommer « {action.cible.label} »</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                L'identifiant <strong>{action.cible.id}</strong> ne change pas. Les exercices
                et la progression des élèves ne sont pas touchés, et l'ancien libellé reste
                résolvable.
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                label="Nouveau libellé"
                value={saisie}
                onChange={(e) => setSaisie(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAction(null)} disabled={enregistrement}>Annuler</Button>
              <Button
                variant="contained"
                disabled={enregistrement || !saisie.trim()}
                onClick={() =>
                  executer(
                    () =>
                      apiRequest('patch', `/admin/taxonomy/notions/${action.cible.id}/label`, { label: saisie.trim() }),
                    `Notion renommée en « ${saisie.trim()} »`
                  )
                }
              >
                Renommer
              </Button>
            </DialogActions>
          </>
        )}

        {action?.type === 'description' && (
          <>
            <DialogTitle>Description de « {action.cible.label} »</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Résumé pédagogique affiché aux parents (et dans le détail notion de l'app).
                Ce n'est <strong>pas</strong> le prompt de génération LLM. Laisser vide pour
                effacer.
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={4}
                maxRows={12}
                label="Description"
                value={saisie}
                onChange={(e) => setSaisie(e.target.value)}
                helperText={`${saisie.length} / 4000`}
                inputProps={{ maxLength: 4000 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAction(null)} disabled={enregistrement}>Annuler</Button>
              <Button
                variant="contained"
                disabled={enregistrement}
                onClick={() =>
                  executer(
                    () =>
                      apiRequest('patch', `/admin/taxonomy/notions/${action.cible.id}/description`, {
                        description: saisie.trim(),
                      }),
                    saisie.trim()
                      ? `Description de « ${action.cible.label} » enregistrée`
                      : `Description de « ${action.cible.label} » effacée`
                  )
                }
              >
                Enregistrer
              </Button>
            </DialogActions>
          </>
        )}

        {action?.type === 'rename-competence' && (
          <>
            <DialogTitle>Renommer la compétence « {action.cible.label} »</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                label="Nouveau libellé"
                value={saisie}
                onChange={(e) => setSaisie(e.target.value)}
                sx={{ mt: 1 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAction(null)} disabled={enregistrement}>Annuler</Button>
              <Button
                variant="contained"
                disabled={enregistrement || !saisie.trim()}
                onClick={() =>
                  executer(
                    () =>
                      apiRequest('patch', `/admin/taxonomy/competences/${action.cible.id}/label`, { label: saisie.trim() }),
                    'Compétence renommée'
                  )
                }
              >
                Renommer
              </Button>
            </DialogActions>
          </>
        )}

        {action?.type === 'reclass' && (
          <>
            <DialogTitle>Déplacer « {action.cible.label} »</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                La notion change de compétence sans changer d'identifiant — les exercices
                existants la suivent.
              </DialogContentText>
              <Select fullWidth value={saisie} onChange={(e) => setSaisie(e.target.value)}>
                {toutesCompetences.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.label} ({c.id})
                  </MenuItem>
                ))}
              </Select>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAction(null)} disabled={enregistrement}>Annuler</Button>
              <Button
                variant="contained"
                disabled={enregistrement || saisie === action.cible.competenceId}
                onClick={() =>
                  executer(
                    () =>
                      apiRequest('patch', `/admin/taxonomy/notions/${action.cible.id}/competence`, { competenceId: saisie }),
                    'Notion déplacée'
                  )
                }
              >
                Déplacer
              </Button>
            </DialogActions>
          </>
        )}

        {action?.type === 'level' && (
          <>
            <DialogTitle>Niveau d'introduction de « {action.cible.label} »</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Détermine à partir de quel niveau la notion est proposée. Le cycle en est
                déduit.
              </DialogContentText>
              <Select fullWidth value={saisie} onChange={(e) => setSaisie(e.target.value)}>
                {ordreNiveaux.map((niveau) => (
                  <MenuItem key={niveau} value={niveau}>{niveau}</MenuItem>
                ))}
              </Select>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAction(null)} disabled={enregistrement}>Annuler</Button>
              <Button
                variant="contained"
                disabled={enregistrement}
                onClick={() =>
                  executer(
                    () =>
                      apiRequest('patch', `/admin/taxonomy/notions/${action.cible.id}/level`, { level: saisie }),
                    'Niveau modifié'
                  )
                }
              >
                Enregistrer
              </Button>
            </DialogActions>
          </>
        )}

        {action?.type === 'deprecate' && (
          <>
            <DialogTitle>Déprécier « {action.cible.label} »</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                La notion n'est plus proposée, mais elle <strong>n'est pas supprimée</strong> :
                des élèves l'ont travaillée et leur progression la référence. Indiquer une
                notion de remplacement permet de rattacher cette progression plus tard.
              </DialogContentText>
              <Select
                fullWidth
                displayEmpty
                value={saisie}
                onChange={(e) => setSaisie(e.target.value)}
              >
                <MenuItem value="">Aucun remplacement</MenuItem>
                {(referentiel?.notions || [])
                  .filter((n) => n.id !== action.cible.id && n.actif !== false)
                  .map((n) => (
                    <MenuItem key={n.id} value={n.id}>{n.label}</MenuItem>
                  ))}
              </Select>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAction(null)} disabled={enregistrement}>Annuler</Button>
              <Button
                variant="contained"
                color="warning"
                disabled={enregistrement}
                onClick={() =>
                  executer(
                    () =>
                      apiRequest('post', `/admin/taxonomy/notions/${action.cible.id}/deprecate`, { replacedById: saisie || null }),
                    'Notion dépréciée'
                  )
                }
              >
                Déprécier
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={!!message}
        autoHideDuration={4000}
        onClose={() => setMessage(null)}
        message={message}
      />
    </Box>
    </AdminLayout>
  );
}
